import { useMemo } from 'react';
import { usePayment } from '$api-clients/hooks';
import {
  buildReportingSummaryPerformanceQuery,
  getPercentage,
} from '../../helpers';
import { useAllTimeStartDate } from '../../ReportsConfiguration/useReportsConfiguration';
import type { ReportingSummaryResult } from '../../types';
import { usePerformanceReportContext } from '../context/PerformanceReportContext';

/** View model row for the Lead Source Performance grid */
export interface LeadSourcePerformanceRow {
  id: string;
  leadSource: string;
  totalTransactions: number | null;
  salesVolume: number | null;
  averageSaleValue: number | null;
  adjustedGci: number | null;
  totalSalesVolume: number | null;
  salesVolumePercent: number;
  averageCommissionAmount: number | null;
}

function mapApiRowToLeadSourcePerformanceRow(
  row: ReportingSummaryResult,
  totalSalesVolume: number,
): LeadSourcePerformanceRow {
  const leadSourceName = row.leadSourceName ?? 'Unknown';
  const salesVolume = row.salesVolume;
  const sides = row.sides;
  const averageSaleValue = row.averageSalesValue;
  const adjustedGci = row.adjustedGrossCommission;

  return {
    id: leadSourceName,
    leadSource: leadSourceName,
    totalTransactions: sides,
    salesVolume,
    averageSaleValue,
    adjustedGci,
    totalSalesVolume,
    salesVolumePercent: getPercentage(salesVolume, totalSalesVolume),
    averageCommissionAmount: row.averageCommissionAmount,
  };
}

/**
 * Fetches lead source performance from GET /reporting/summary with groupBy: LEAD_SOURCE,
 * using Performance report filters from context. Returns rows with % of total sales volume.
 */
export function useLeadSourcePerformance(viewReportAs: string) {
  const { filters } = usePerformanceReportContext();
  const allTimeStartDate = useAllTimeStartDate();
  const { data, isLoading, error } = usePayment('/reporting/summary', {
    params: {
      query: {
        ...buildReportingSummaryPerformanceQuery(
          filters,
          viewReportAs,
          allTimeStartDate,
        ),
        offset: 0,
        limit: 1000,
        transactionType: ['LISTING', 'OFFER'],
        groupBy: ['LEAD_SOURCE'],
      },
    },
  });

  return useMemo(() => {
    const results = data?.results ?? [];
    const totalSalesVolume = results.reduce(
      (sum, row) => sum + (row.salesVolume ?? 0),
      0,
    );
    const rows = results.map((row) =>
      mapApiRowToLeadSourcePerformanceRow(row, totalSalesVolume),
    );

    return {
      rows,
      isLoading,
      error,
    };
  }, [data?.results, isLoading, error]);
}
